import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import PropTypes from 'prop-types';
import callOracleApi from '../../utils/call-oracle-api.js';
import useCurrentUser from '../../hooks/useCurrentUser.js';
import '../../styles/MissionRequests.css';

const STATUS_LABELS = {
    pending: { label: 'Under Review', color: '#f39c12', bg: '#fef9e7' },
    approved: { label: 'Approved', color: '#27ae60', bg: '#eafaf1' },
    rejected: { label: 'Not Approved', color: '#c0392b', bg: '#fdedec' }
};

const MyMissionRequestsPage = () => {
    const navigate = useNavigate();
    const { isLeadership } = useCurrentUser();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                const response = await callOracleApi(
                    '/mission-requests/my',
                    'GET'
                );
                const data = await response.json();
                if (data.success) setRequests(data.payload);
            } catch {
                toast.error('Failed to load your submissions');
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    if (loading)
        return (
            <div className="mr-page">
                <p>Loading...</p>
            </div>
        );

    return (
        <div className="mr-page">
            <div className="mr-page-header">
                <div>
                    <h1 className="mr-page-title">My Mission Submissions</h1>
                    <p className="mr-page-subtitle">
                        Track the status of your submitted mission ideas
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                    {isLeadership && (
                        <button
                            className="btn-secondary"
                            onClick={() =>
                                navigate('/mission-requests/review')
                            }>
                            Review Queue
                        </button>
                    )}
                    <button
                        className="btn-primary"
                        onClick={() => navigate('/mission-requests/submit')}>
                        + New Submission
                    </button>
                </div>
            </div>

            {requests.length === 0 ? (
                <div
                    style={{
                        textAlign: 'center',
                        padding: '4rem 2rem',
                        background: '#fff',
                        border: '1px solid #e0e0e0',
                        borderRadius: 8
                    }}>
                    <p
                        style={{
                            fontSize: '1rem',
                            color: '#888',
                            marginBottom: '1rem'
                        }}>
                        You haven&apos;t submitted any missions yet.
                    </p>
                    <button
                        className="btn-primary"
                        onClick={() => navigate('/mission-requests/submit')}>
                        Submit a Mission Idea
                    </button>
                </div>
            ) : (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '0.75rem'
                    }}>
                    {requests.map((req) => {
                        const s =
                            STATUS_LABELS[req.status] || STATUS_LABELS.pending;
                        const isOpen = expanded === req.id;
                        return (
                            <div key={req.id} className="mr-list-card">
                                <div
                                    className="mr-card-header"
                                    onClick={() =>
                                        setExpanded(isOpen ? null : req.id)
                                    }>
                                    <div>
                                        <p className="mr-card-title">
                                            {req.title}
                                        </p>
                                        <p className="mr-card-meta">
                                            Submitted{' '}
                                            {req.createdAt
                                                ? new Date(
                                                      req.createdAt
                                                  ).toLocaleDateString()
                                                : '—'}
                                        </p>
                                    </div>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 10
                                        }}>
                                        <span
                                            className="mr-status-badge"
                                            style={{
                                                color: s.color,
                                                background: s.bg
                                            }}>
                                            {s.label}
                                        </span>
                                        <span
                                            style={{
                                                color: '#bbb',
                                                fontSize: '0.9rem'
                                            }}>
                                            {isOpen ? '▲' : '▼'}
                                        </span>
                                    </div>
                                </div>

                                {isOpen && (
                                    <div className="mr-card-body">
                                        {/* Mission Info */}
                                        <div className="mr-detail-section">
                                            <p className="mr-detail-section-title">
                                                Mission Info
                                            </p>
                                            <div className="mr-detail-grid">
                                                <DetailItem
                                                    label="Problem to Solve"
                                                    value={req.problemStatement}
                                                    full
                                                />
                                                <DetailItem
                                                    label="Description"
                                                    value={req.description}
                                                    full
                                                />
                                                <DetailItem
                                                    label="Executive Objective"
                                                    value={
                                                        req.executiveObjective
                                                    }
                                                    full
                                                />
                                                <DetailItem
                                                    label="Estimated Weeks"
                                                    value={req.estimatedWeeks}
                                                />
                                                <DetailItem
                                                    label="Success Measure"
                                                    value={req.successMeasure}
                                                    full
                                                />
                                                <DetailItem
                                                    label="Estimated ROI"
                                                    value={req.estimatedRoi}
                                                    full
                                                />
                                            </div>
                                        </div>

                                        {/* Mission Team */}
                                        <div className="mr-detail-section">
                                            <p className="mr-detail-section-title">
                                                Mission Team
                                            </p>
                                            <div className="mr-detail-grid">
                                                <DetailItem
                                                    label="Potential Captain"
                                                    value={req.potentialCaptain}
                                                />
                                                <DetailItem
                                                    label="Interested Members"
                                                    value={
                                                        req.interestedMembers
                                                    }
                                                />
                                                <DetailItem
                                                    label="Departments Involved"
                                                    value={
                                                        req.involvedDepartments
                                                            ?.length
                                                            ? req.involvedDepartments.join(
                                                                  ', '
                                                              )
                                                            : '—'
                                                    }
                                                    full
                                                />
                                            </div>
                                        </div>

                                        {/* Development Needs */}
                                        <div className="mr-detail-section">
                                            <p className="mr-detail-section-title">
                                                Development Needs
                                            </p>
                                            <div className="mr-detail-grid">
                                                <DetailItem
                                                    label="Innovation Priority"
                                                    value={`${req.innovationPriority} / 5`}
                                                />
                                                <DetailItem
                                                    label="Hub Priority"
                                                    value={`${req.hubPriority} / 5`}
                                                />
                                                {req.innovationNeeds && (
                                                    <DetailItem
                                                        label="Innovation Needs"
                                                        value={
                                                            req.innovationNeeds
                                                        }
                                                        full
                                                    />
                                                )}
                                                {req.hubNeeds && (
                                                    <DetailItem
                                                        label="Hub Needs"
                                                        value={req.hubNeeds}
                                                        full
                                                    />
                                                )}
                                            </div>
                                        </div>

                                        {/* Leadership feedback */}
                                        {req.leadershipNotes && (
                                            <div
                                                className="mr-feedback-box"
                                                style={{
                                                    borderColor: s.color,
                                                    background: s.bg
                                                }}>
                                                <p
                                                    className="mr-feedback-box-label"
                                                    style={{ color: s.color }}>
                                                    Leadership Feedback
                                                </p>
                                                <p
                                                    style={{
                                                        margin: 0,
                                                        fontSize: '0.9rem'
                                                    }}>
                                                    {req.leadershipNotes}
                                                </p>
                                            </div>
                                        )}

                                        {req.status === 'approved' &&
                                            req.qProfitPoints != null && (
                                                <div className="mr-qpoints-box">
                                                    <span
                                                        style={{
                                                            fontWeight: 600,
                                                            color: '#27ae60'
                                                        }}>
                                                        Q Profit Points:
                                                    </span>
                                                    <span
                                                        style={{
                                                            fontWeight: 700,
                                                            fontSize: '1.1rem'
                                                        }}>
                                                        {req.qProfitPoints}
                                                    </span>
                                                </div>
                                            )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const DetailItem = ({ label, value, full }) => (
    <div className={`mr-detail-item${full ? ' full' : ''}`}>
        <span className="mr-detail-label">{label}</span>
        <span className="mr-detail-value">{value || '—'}</span>
    </div>
);

DetailItem.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    full: PropTypes.bool
};

export default MyMissionRequestsPage;
